import * as Yup from "yup";
import classNames from "classnames";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Modal } from "../../../molecules";
import { actions as modalActions } from "../../Modal/modal.reducers";
import {
  DEPLOY_STATUSES,
  EMPTY_STRING,
  BACKEND_INITIAL_STATUS,
  BACKEND_FAILURE_STATUS,
} from "../../../../constants";
import { MODAL_SIZE } from "../modals.constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import {
  setMulesoftDeployStatus,
  selectSelectedIntegration,
} from "../../../../pages/Mulesoft";
import { deployBEApp, setPipelineStatus } from "../../../../pages/DataModels";
import { showPasswordIcon, hidePasswordIcon } from "../../../../assets/icons";
import { DRAWERS } from "../../AppDrawers/drawers.constants";
import { setSelectedDrawer } from "../../Drawer";
import { VALIDATION_MESSAGES } from "../../../../constants";

const { setSelectedModal } = modalActions;
const { MD } = MODAL_SIZE;

const { DEPLOY_BE_APP } = SPINNER_NAMES;
const { FAILURE, IN_PROGRESS, CREATING_PROJECT } = DEPLOY_STATUSES;
const { DEPLOYMENT_LOGS_DRAWER } = DRAWERS;
const { DEPLOY_MESSAGE_REQUIRED, PASSWORD_NOT_MATCHED, PASSWORD_REQUIRED } = VALIDATION_MESSAGES

const DeployMulesoftEndpointsModal = () => {
  const dispatch = useDispatch();
  const selectedIntegration = useSelector(selectSelectedIntegration);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [showPassword, setHidePassword] = useState(false);
  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
  }, [dispatch]);

  const initialValues = {
    deployMessage: EMPTY_STRING,
    password: EMPTY_STRING,
  };

  const validationSchema = Yup.object({
    deployMessage: Yup.string().required(DEPLOY_MESSAGE_REQUIRED),
    password: Yup.string()
      .matches(
        process.env.REACT_APP_DEPLOY_BE_PASSWORD,
        PASSWORD_NOT_MATCHED
      )
      .required(PASSWORD_REQUIRED),
  });

  const handleOnSubmit = useCallback(
    (values) => {
      const { name, id } = selectedIntegration;
      const { deployMessage } = values;
      handleClose();
      const data = {
        integrationId: id,
        dataModelId: EMPTY_STRING,
        appName: name,
        hasExternalSource: true,
        deployMessage,
      };
      dispatch(setMulesoftDeployStatus(CREATING_PROJECT));
      dispatch(setPipelineStatus({ backend: BACKEND_INITIAL_STATUS }));
      dispatch(setSelectedDrawer(DEPLOYMENT_LOGS_DRAWER));
      dispatch(deployBEApp(data)).then((res) => {
        if (res.payload.backend) dispatch(setMulesoftDeployStatus(IN_PROGRESS));
        else {
          dispatch(setPipelineStatus({ backend: BACKEND_FAILURE_STATUS }));
          dispatch(setMulesoftDeployStatus(FAILURE));
        }
      });
    },
    [dispatch, handleClose, selectedIntegration]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ values, handleReset, touched, errors }) => (
        <Modal
          title="Deploy Mulesoft Endpoints"
          handleReset={handleReset}
          handleClose={handleClose}
          size={MD}
        >
          <Form>
            <div className="p-6">
              <div className="pb-4">
                <div>
                  <label className="text-sm font-medium text-gray-50 mb-1 block">
                    Deploy Message
                  </label>
                  <div className="flex flex-between items-center bg-white rounded-md">
                    <Field
                      name="deployMessage"
                      placeholder="Deploy Message"
                      className={classNames("form-control !ring-0 border-0", {
                        "border-red-600":
                          touched.deployMessage && errors.deployMessage,
                      })}
                      type="text"
                      value={values.deployMessage}
                    />
                  </div>
                  {touched.deployMessage && errors.deployMessage && (
                    <div className="font-medium text-red-500 text-sm">
                      {errors.deployMessage}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-50 mb-1 block">
                    Enter Password to deploy
                  </label>
                  <div
                    className={classNames(
                      "flex flex-between items-center bg-white rounded-md border-2",
                      {
                        "border-red-600":
                          touched.password && errors.password,
                      }
                    )}
                  >
                    <Field
                      name="password"
                      placeholder="Password"
                      className="form-control !ring-0 border-0"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                    />
                    <img
                      className=" w-8 h-4 pr-2 cursor-pointer"
                      onClick={() => setHidePassword(!showPassword)}
                      src={showPassword ? showPasswordIcon : hidePasswordIcon}
                      alt="show Password"
                    />
                  </div>
                  {touched.password && errors.password && (
                    <div className="font-medium text-red-500 text-sm">
                      {errors.password}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-700 flex align-center justify-end">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isSpinnerActive}
              >
                <Spinner
                  name={DEPLOY_BE_APP}
                  setIsSpinnerActive={setIsSpinnerActive}
                  persistSize
                >
                  Submit
                </Spinner>
              </button>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default DeployMulesoftEndpointsModal;
